import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { DeviceFrame, framedViewerUrl } from '../authoring/DeviceFrame';
import { useViewerFrame } from '../authoring/viewer-frame';
import { env } from '../env';
import { reviewQuery } from '../library/sources';

import { previewSample, type TopicsReady } from './config';

import type { OnboardingConfig } from '@platform/schema/onboarding';

/**
 * The phone on the right — ONB-19. An iframe of `apps/viewer`, which renders onboarding
 * through the same `@platform/player` step the app does. The console posts the config it
 * is editing, so an edit shows here before it is saved, and the viewer asks no server.
 */
export function FlowPreview({
  config,
  stepId,
  nonce,
  challengeId,
  topicsReady,
}: {
  config: OnboardingConfig;
  /** The step to show; none plays the whole journey from Welcome. */
  stepId: string | undefined;
  /** Bumped to restart the preview on the same step. */
  nonce: number;
  challengeId: string | undefined;
  /** Which of the preview learner's interests can start. */
  topicsReady: TopicsReady;
}) {
  const challenge = useQuery({
    ...reviewQuery(challengeId ?? ''),
    enabled: challengeId !== undefined,
  });

  const message = useMemo(
    () => ({
      type: 'onboarding' as const,
      scheme: 'light' as const,
      onboarding: {
        flow: config.flow,
        copy: config.copy,
        suggestions: config.suggestions,
        stepId,
        challenge: challenge.data,
        nonce,
        sample: previewSample(config.flow, topicsReady),
      },
    }),
    [config, stepId, nonce, challenge.data, topicsReady],
  );
  const { frame } = useViewerFrame(message);

  if (env.VITE_VIEWER_URL === undefined) {
    return (
      <Alert severity="info">
        The preview is unavailable: VITE_VIEWER_URL is unset.
      </Alert>
    );
  }
  return (
    <DeviceFrame>
      <Box
        component="iframe"
        ref={frame}
        src={framedViewerUrl(env.VITE_VIEWER_URL)}
        title="Onboarding preview"
        sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </DeviceFrame>
  );
}
